import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatStream } from './components/ChatStream'
import { MethodologyPanel } from './components/MethodologyPanel'
import { SiteNav } from './components/SiteNav'
import { SiteFooter } from './components/SiteFooter'
import {
  clearConversationId,
  isMinorBlockedLocal,
  loadSession,
  streamChat,
  streamPlan,
  writeConversationId,
} from './lib/api'
import { trackAdsConversionOnce } from './lib/google-ads'
import { buildResumedMessages } from './lib/resume'
import type { ChatUIMessage } from './lib/types'
import { getStrings, useLang } from './i18n'

const CONVERSATION_ID_KEY = 'tdee_conversation_id'

// Debug surface: a `/tdee/sessions/<uuid>` URL hydrates the app from a stored
// conversation instead of starting fresh. The UUID v4 shape gates parsing —
// anything else falls through to the normal flow.
const SESSION_PATH_RE =
  /\/sessions\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

function readSessionIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const match = window.location.pathname.match(SESSION_PATH_RE)
  return match ? match[1] : null
}

// Gate for the US CTA entrance choreography (beat + fade/slide-up). When the
// user asks for reduced motion we skip both: the card renders immediately
// and the .cta-card-enter animation is disabled in CSS via the same query.
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function App({
  glp1 = false,
  embed = false,
}: { glp1?: boolean; embed?: boolean } = {}) {
  // UI locale from the URL path (/tdee/ = en, /tdee/es/ = es). Drives every
  // user-visible string and is sent to the server so the interview + plan run
  // in the same language.
  const lang = useLang()
  const t = getStrings(lang)
  const [messages, setMessages] = useState<ChatUIMessage[]>([])
  const [streamingText, setStreamingText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [methodologyOpen, setMethodologyOpen] = useState(false)
  // Captured from the SSE 'session' event so PostResultCapture can address the
  // exact conversation when submitting the email form.
  const [conversationId, setConversationId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CONVERSATION_ID_KEY)
    } catch {
      return null
    }
  })
  // Under-18 guardrail: sticky, browser-local mirror of the server-side block.
  // Initialized from the persisted flag so a returning minor lands locked
  // before the first keystroke; set to true whenever a chat turn streams the
  // `minor_blocked` SSE event. Never reset by "Recalculate" — the only path
  // back is emailing support@nanorhino.com. Disables the composer and shows a
  // block notice.
  const [minorBlocked, setMinorBlocked] = useState<boolean>(() => isMinorBlockedLocal())
  // Resume-on-reload: true while the mount effect below is (potentially)
  // restoring a prior conversation. Locks the composer + start buttons for
  // the fetch round-trip so a returning user can't fire the seed message into
  // their existing conversation during the gap — that exact race is how
  // reloads used to restart the whole interview inside the same conversation
  // row. Initialized true only when there is something to resume, so brand-new
  // visitors never see a locked composer.
  const [hydrating, setHydrating] = useState<boolean>(() => {
    if (embed) return false
    if (readSessionIdFromUrl()) return true
    try {
      return Boolean(localStorage.getItem(CONVERSATION_ID_KEY))
    } catch {
      return false
    }
  })

  const handleSend = useCallback(
    async (text: string) => {
      setError(null)
      const next: ChatUIMessage[] = [
        ...messages,
        { role: 'user', kind: 'text', content: text },
      ]
      setMessages(next)
      setBusy(true)
      setStreamingText('')
      let accumulated = ''
      // The calc card is rendered immediately when result_payload arrives,
      // so the user can read the numbers while the post-tool question types
      // out below it.
      let planRequested = false
      // Plan-iteration: when the LLM detects the user is asking for plan
      // changes ("make it vegetarian"), the server forwards a `modification`
      // string on plan_request. The same plan-stream pipeline runs, but the
      // new plan card gets appended below the prior one — the user sees the
      // history of iterations in the chat.
      let planModification: string | null = null
      // Server signals this once it has finished streaming the post-calc plan
      // question. We use it to append a one-tap "Yes, make my plan" chip so
      // users don't have to type "yes" — the dominant drop-off point in the
      // 05-23 → 05-25 cohort was users leaving the question unanswered.
      let showPlanChip = false
      // US funnel (2026-06-10): the server skipped the plan offer entirely —
      // the CTA card (SMS claim) goes right after the calc card instead of
      // after a plan stream. Mutually exclusive with showPlanChip; the server
      // emits one or the other.
      let showSmsCta = false
      try {
        await streamChat({
          messages: next,
          landing: glp1 ? 'glp1' : undefined,
          lang,
          onEvent: (ev) => {
            if (ev.event === 'session') {
              setConversationId(ev.data.conversation_id)
            } else if (ev.event === 'text') {
              accumulated += ev.data.delta
              setStreamingText(accumulated)
            } else if (ev.event === 'plan_request') {
              planRequested = true
              if (ev.data.modification) planModification = ev.data.modification
            } else if (ev.event === 'show_plan_chip') {
              showPlanChip = true
            } else if (ev.event === 'show_sms_cta') {
              showSmsCta = true
            } else if (ev.event === 'result_payload') {
              // Tool result arrived mid-stream. Flush any pre-tool narrative
              // first, then render the calc card immediately so the user has
              // it on-screen while the post-tool question types out below.
              // Ads funnel: first result this session = tdee_result_viewed
              // (recalcs are deduped inside the helper).
              trackAdsConversionOnce('tdee_result_viewed')
              const preText = accumulated
              accumulated = ''
              setStreamingText('')
              setMessages((prev) => {
                const out: ChatUIMessage[] = [...prev]
                if (preText.trim()) {
                  out.push({ role: 'assistant', kind: 'text', content: preText })
                }
                out.push({ role: 'assistant', kind: 'calc', payload: ev.data })
                return out
              })
            } else if (ev.event === 'minor_blocked') {
              // Under-18 guardrail tripped: lock the composer. The refusal
              // itself arrives as normal `text` events and commits as an
              // assistant bubble when the stream ends; this only flips the
              // sticky UI state (api.ts already persisted the browser flag).
              setMinorBlocked(true)
            } else if (ev.event === 'tool_error') {
              setError(ev.data.message)
            } else if (ev.event === 'error') {
              setError(ev.data.message)
            }
          },
        })
        // Stream ended. Commit whatever post-tool text accumulated as the
        // final bubble — the card was already rendered when result_payload
        // arrived, so the question lands below it. The plan-offer chip (if
        // server emitted show_plan_chip) lands directly under the question
        // bubble, so we append both inside the same updater to keep the
        // visual order stable.
        // Embed widget: keep the calc card + the model's spoken interpretation,
        // but never mount the plan-offer chip / SMS CTA / plan stream — the
        // embedded tool ends at the result, with a single lightweight
        // "get coached" link handled by ChatStream (see its `embed` prop).
        if (accumulated || (!embed && showPlanChip && !planRequested)) {
          setMessages((prev) => {
            const out: ChatUIMessage[] = [...prev]
            if (accumulated) {
              out.push({ role: 'assistant', kind: 'text', content: accumulated })
            }
            if (!embed && showPlanChip && !planRequested) {
              out.push({ role: 'assistant', kind: 'plan_offer' })
            }
            return out
          })
        }

        // US SMS CTA (show_sms_cta): mounts only after the streamed two-beat
        // bridge has fully committed above, held for a ~500ms beat so the
        // announcement lands before the card enters (then .cta-card-enter
        // fades + slides it up — see index.css). Reduced motion: no beat, no
        // animation, immediate render. Token/country/TDEE come from the calc
        // payload exactly like the post-plan append path does.
        if (!embed && showSmsCta) {
          if (!prefersReducedMotion()) {
            await new Promise((r) => setTimeout(r, 500))
          }
          setMessages((prev) => {
            // Dedupe: since the 2026-08-08 funnel unification the server can
            // emit show_sms_cta outside the calc turn too (legacy pending-offer
            // "yes" fast path) — never mount a second CTA card for the same
            // calc.
            const lastCalcIdx = prev.map((m) => m.kind).lastIndexOf('calc')
            if (prev.some((m, i) => i > lastCalcIdx && m.kind === 'cta')) return prev
            const calcMsg = lastCalcIdx >= 0 ? prev[lastCalcIdx] : null
            const payload = calcMsg && calcMsg.kind === 'calc' ? calcMsg.payload : null
            return [
              ...prev,
              {
                role: 'assistant',
                kind: 'cta',
                handoffToken: payload?.handoff_token ?? null,
                country: payload?.country ?? null,
                tdeeRecommended: payload?.tdee_recommended ?? null,
                tdeeLow: payload?.tdee_low ?? null,
                tdeeHigh: payload?.tdee_high ?? null,
                isMinor: payload?.is_minor ?? false,
                hasExistingHandoff: payload?.has_existing_handoff ?? false,
                isGlp1: payload?.on_glp1 ?? false,
                intent: payload?.intent,
                honestPlan: payload?.honest_plan ?? null,
              },
            ]
          })
        }

        // If the LLM called generate_plan, append a streaming plan placeholder
        // then kick off the plan stream (same path as the manual button click).
        // For iterations the placeholder lands below the prior plan, so the
        // user sees both the original and the revised version in the chat.
        if (!embed && planRequested) {
          setMessages((prev) => {
            planMessageIndexRef.current = prev.length
            return [...prev, { role: 'assistant', kind: 'plan', markdown: '', status: 'streaming' }]
          })
          await runPlanStream(planModification)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'chat failed')
      } finally {
        setStreamingText('')
        setBusy(false)
      }
    },
    [messages, glp1, lang]
  )

  // Tracks which message index the plan stream is updating. Set inside the
  // setMessages updater so React's render commit resolves it synchronously
  // BEFORE the network round-trip completes — the SSE callbacks then read it
  // back as the current target index.
  const planMessageIndexRef = useRef<number | null>(null)

  // Stream the plan into the message at planMessageIndexRef.current and append
  // the post-plan CTA card on completion. Shared by the initial generate path
  // and the retry path; both set the ref synchronously inside a setMessages
  // updater before calling this.
  //
  // Pacing: even if the server returns the whole plan in a single SSE chunk
  // (cached response, no LLM latency), we buffer it and emit ~one word per
  // tick so the user can actually read along instead of being hit with a
  // wall of markdown. ~30ms/word ≈ 30+ words/sec, which feels alive without
  // being annoying.
  const runPlanStream = useCallback(async (modification?: string | null) => {
    if (!conversationId) return
    const updatePlan = (
      mut: (
        m: Extract<ChatUIMessage, { kind: 'plan' }>
      ) => Extract<ChatUIMessage, { kind: 'plan' }>
    ) => {
      const idx = planMessageIndexRef.current
      if (idx == null) return
      setMessages((prev) => {
        const m = prev[idx]
        if (!m || m.kind !== 'plan') return prev
        const next = [...prev]
        next[idx] = mut(m)
        return next
      })
    }

    // Producer side: network deltas drop into `buffer`. Consumer loop below
    // pulls roughly one word per tick out of it.
    let buffer = ''
    let networkDone = false
    let networkErr: string | null = null
    const networkP = streamPlan({
      conversationId,
      kind: 'plan',
      modification: modification ?? null,
      lang,
      onText: (delta) => {
        buffer += delta
      },
      onDone: () => {
        networkDone = true
      },
      onError: (msg) => {
        networkErr = msg
        networkDone = true
      },
    })
      .then(() => {
        networkDone = true
      })
      .catch((e: unknown) => {
        networkErr = e instanceof Error ? e.message : 'plan request failed'
        networkDone = true
      })

    // Consumer side: drip-feed the message until both the network is done
    // AND the buffer is empty. Word boundary heuristic: stop on whitespace
    // so chunks land at natural reading breaks, or after 10 chars max.
    const TICK_MS = 30
    while (!networkDone || buffer.length > 0) {
      if (buffer.length === 0) {
        await new Promise((r) => setTimeout(r, TICK_MS))
        continue
      }
      let i = 0
      while (i < buffer.length && i < 10) {
        const c = buffer[i]
        i++
        if (c === ' ' || c === '\n') break
      }
      const chunk = buffer.slice(0, i)
      buffer = buffer.slice(i)
      updatePlan((m) => ({ ...m, markdown: m.markdown + chunk }))
      await new Promise((r) => setTimeout(r, TICK_MS))
    }
    await networkP

    if (networkErr) {
      updatePlan((m) => ({ ...m, status: 'error', error: networkErr! }))
    } else {
      updatePlan((m) =>
        m.status === 'streaming' ? { ...m, status: 'done' } : m
      )
      // Ads funnel: plan stream fully delivered (network done + buffer
      // drained) with no error = plan_generated.
      trackAdsConversionOnce('plan_generated')
    }
    // After plan finishes (success or error), append the AI-nutrition-coach
    // CTA card. Pull handoff_token + country from the most recent calc card
    // so the CTA can render the right surface. Since the 2026-06-10 US funnel
    // redesign this path is effectively intl-only (email capture) — US users
    // get the CTA straight after the calc card via show_sms_cta and never
    // stream a plan — but it stays country-agnostic so legacy/edge sessions
    // that do stream a plan still get a CTA.
    const idx = planMessageIndexRef.current
    if (idx == null) return
    setMessages((prev) => {
      const planMsg = prev[idx]
      if (!planMsg || planMsg.kind !== 'plan' || planMsg.status !== 'done') {
        return prev
      }
      // Don't double-append if a CTA card is already the next message.
      if (prev[idx + 1]?.kind === 'cta') return prev
      const calcMsg = [...prev].reverse().find((m) => m.kind === 'calc')
      const payload = calcMsg && calcMsg.kind === 'calc' ? calcMsg.payload : null
      return [
        ...prev,
        {
          role: 'assistant',
          kind: 'cta',
          handoffToken: payload?.handoff_token ?? null,
          country: payload?.country ?? null,
          tdeeRecommended: payload?.tdee_recommended ?? null,
          tdeeLow: payload?.tdee_low ?? null,
          tdeeHigh: payload?.tdee_high ?? null,
          isMinor: payload?.is_minor ?? false,
          hasExistingHandoff: payload?.has_existing_handoff ?? false,
          isGlp1: payload?.on_glp1 ?? false,
          intent: payload?.intent,
          honestPlan: payload?.honest_plan ?? null,
        },
      ]
    })
  }, [conversationId, lang])

  const handleRequestPlan = useCallback(async () => {
    if (!conversationId) return
    setMessages((prev) => {
      planMessageIndexRef.current = prev.length
      return [
        ...prev,
        { role: 'assistant', kind: 'plan', markdown: '', status: 'streaming' },
      ]
    })
    await runPlanStream()
  }, [conversationId, runPlanStream])

  // Retry handler: reset the most-recent plan message back to streaming and
  // re-stream into the same slot. Used by the inline "Try again" link.
  const handleRetryPlan = useCallback(() => {
    if (!conversationId) return
    const idx = planMessageIndexRef.current
    if (idx == null) return
    setMessages((prev) => {
      const m = prev[idx]
      if (!m || m.kind !== 'plan') return prev
      const next = [...prev]
      next[idx] = { ...m, markdown: '', status: 'streaming', error: undefined }
      // Also strip any CTA that was appended after the prior failed plan.
      return next.filter((msg, i) => !(i > idx && msg.kind === 'cta'))
    })
    void runPlanStream()
  }, [conversationId, runPlanStream])

  const handleRecalculate = useCallback(() => {
    clearConversationId()
    setMessages([])
    setStreamingText('')
    setError(null)
    setConversationId(null)
    planMessageIndexRef.current = null
    // If we hydrated from /sessions/<uuid>, drop that segment so refresh
    // after Recalculate starts a fresh conversation instead of reloading
    // the same session.
    if (typeof window !== 'undefined' && SESSION_PATH_RE.test(window.location.pathname)) {
      try {
        window.history.replaceState(null, '', import.meta.env.BASE_URL)
      } catch {
        /* ignore */
      }
    }
  }, [])

  // Mobile IM shell (<=767px, see "MOBILE IM LAYOUT" in index.css): mirror
  // the visual viewport into CSS custom properties so the fixed chat shell
  // tracks the iOS Safari keyboard. iOS does NOT resize the layout viewport
  // when the keyboard opens — 100dvh stays full-height and a bottom-fixed
  // input bar gets covered — so the shell's height reads --vvh (the live
  // visualViewport height) with 100dvh/100vh as fallbacks. While the
  // keyboard is up we also zero out the home-indicator safe-area padding
  // (--kb-safe-bottom) and pin the page scroll so the shell stays glued.
  // Desktop is unaffected: the vars are only consumed inside the mobile
  // media query.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const root = document.documentElement
    const update = () => {
      root.style.setProperty('--vvh', `${Math.round(vv.height)}px`)
      root.style.setProperty('--vv-offset-top', `${Math.round(vv.offsetTop)}px`)
      const keyboardOpen = window.innerHeight - vv.height > 80
      if (keyboardOpen) {
        root.style.setProperty('--kb-safe-bottom', '0px')
        // iOS nudges the layout viewport up when focusing an input near the
        // keyboard; with the shell position:fixed this leaves a dead gap.
        window.scrollTo(0, 0)
      } else {
        root.style.removeProperty('--kb-safe-bottom')
      }
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      root.style.removeProperty('--vvh')
      root.style.removeProperty('--vv-offset-top')
      root.style.removeProperty('--kb-safe-bottom')
    }
  }, [])

  // Conversation restore, one effect for both entry points (runs once per
  // mount):
  //   * /tdee/sessions/<uuid> — the debug surface; loud error on failure.
  //   * Resume-on-reload — a persisted conversation id with no URL override.
  //     iOS Safari discards backgrounded tabs and the SPA keeps no message
  //     state, so before this a returning user landed on a blank chat with a
  //     start button that re-seeded their EXISTING conversation and re-ran
  //     the whole interview (18.5% of CTA-shown sessions, 8/1–8/11, at half
  //     the click-through). Now the transcript + calc card + claim CTA come
  //     back instead. Failures here are silent (the empty state is the old
  //     status quo); a 404 additionally drops the stale id so the next
  //     interview starts a clean row.
  const sessionLoadedRef = useRef(false)
  useEffect(() => {
    if (sessionLoadedRef.current) return
    sessionLoadedRef.current = true
    const urlSessionId = readSessionIdFromUrl()
    const resumeId = urlSessionId ?? (embed ? null : conversationId)
    if (!resumeId) {
      setHydrating(false)
      return
    }
    void (async () => {
      try {
        const data = await loadSession(resumeId)
        if (!data) {
          if (urlSessionId) {
            setError("Couldn't load session: not found")
          } else {
            clearConversationId()
            setConversationId(null)
          }
          return
        }
        const hydrated = buildResumedMessages(data)
        // A conversation row with nothing said yet restores to nothing —
        // leave the normal empty state in place.
        if (hydrated.length === 0) return
        setMessages(hydrated)
        setConversationId(data.conversation_id)
        writeConversationId(data.conversation_id)
      } catch (e) {
        if (urlSessionId) {
          setError(`Couldn't load session: ${e instanceof Error ? e.message : 'unknown'}`)
        }
        // Background resume failure (transient network, etc.): stay silent —
        // the user gets the pre-resume behavior, nothing worse.
      } finally {
        setHydrating(false)
      }
    })()
  }, [])

  // Embed widget: report our rendered height to the host page so its iframe
  // can size to content (no inner scrollbar). Fires on mount and on every
  // layout change — messages growing, the result card mounting, etc.
  useEffect(() => {
    if (!embed) return
    const report = () =>
      window.parent?.postMessage(
        { type: 'nanorhino-embed-height', height: document.documentElement.scrollHeight },
        '*'
      )
    report()
    const ro = new ResizeObserver(report)
    ro.observe(document.documentElement)
    return () => ro.disconnect()
  }, [embed])

  // Drives the mobile IM layout: once a conversation is underway the hero
  // and disclaimer leave the flow (CSS, <=767px only) so the chat owns the
  // whole viewport like an IM thread. Recalculate resets messages → the
  // condensed hero returns. Desktop layout ignores both classes.
  const chatActive = messages.length > 0 || Boolean(streamingText)

  return (
    <div className={embed ? 'embed-page' : chatActive ? 'chat-page chat-page--active' : 'chat-page'}>
      {!embed && <SiteNav mobileLegalLinks lang={lang} />}
      <div className="app">
      {!embed && (
      <header className="page-hero">
        <h1 className="hero-title">
          {glp1 ? (
            lang === 'es' ? (
              <>
                Calculadora de <em>calorías y proteína</em> GLP-1 — con IA.
              </>
            ) : (
              <>
                GLP-1 <em>Calorie &amp; Protein</em> Calculator — Powered by AI.
              </>
            )
          ) : lang === 'es' ? (
            <>
              La calculadora de TDEE <em>más precisa</em> — con IA.
            </>
          ) : (
            <>
              The <em>Most Accurate</em> TDEE Calculator — Powered by AI.
            </>
          )}
        </h1>
        <p className="hero-lede">
          {glp1 ? (
            lang === 'es' ? (
              <>
                Con un GLP-1 — Ozempic, Wegovy, Mounjaro, Zepbound — el apetito ya
                crea el déficit. El verdadero riesgo es <em>perder músculo</em>. Una
                IA entrevista tu día y luego devuelve tus <em>calorías de
                mantenimiento</em> como techo, una <em>meta de proteína</em> que
                preserva músculo, y un piso seguro para que no comas de menos. Cada
                número trazable a una fórmula publicada.
              </>
            ) : (
              <>
                On a GLP-1 — Ozempic, Wegovy, Mounjaro, Zepbound — appetite already
                drives the deficit. The real risk is <em>losing muscle</em>. An AI
                interviews your day, then returns your <em>maintenance calories</em> as
                a ceiling, a muscle-preserving <em>protein target</em>, and a safe
                floor so you don't undereat. Every number traceable to a published
                formula.
              </>
            )
          ) : lang === 'es' ? (
            <>
              El TDEE — <em>gasto energético diario total</em> — son las calorías que
              tu cuerpo realmente quema en 24 horas. Las calculadoras tradicionales lo
              reducen a 1 de 5 niveles de actividad, ocultando un error de 300–500
              kcal/día en cualquier dirección. Aquí, una IA entrevista tu día en 5–7
              preguntas y devuelve un rango <em>calibrado</em> con TMB, NEAT, ETA y
              ejercicio desglosados — cada número trazable a una fórmula publicada.
            </>
          ) : (
            <>
              TDEE — <em>Total Daily Energy Expenditure</em> — is the calories your body actually
              burns in 24 hours. Traditional calculators collapse it into 1 of 5 activity buckets,
              hiding a 300–500 kcal/day error in either direction. Here, an AI interviews your
              day in 5–7 questions and returns a <em>calibrated</em> range with BMR, NEAT, TEF,
              and EAT broken out — every number traceable to a published formula.
            </>
          )}
        </p>
        <div className="hero-meta">
          <div className="badge-strip">
            {(glp1 ? t.hero.badgesGlp1 : t.hero.badgesDefault).map((b) => (
              <span className="badge" key={b}>{b}</span>
            ))}
          </div>
          <button
            type="button"
            className="hero-methodology"
            onClick={() => setMethodologyOpen(true)}
            aria-haspopup="dialog"
          >
            {t.hero.methodologyLink}
          </button>
        </div>
      </header>
      )}

      <main>
        <ChatStream
          messages={messages}
          streamingText={streamingText}
          onSend={handleSend}
          disabled={busy || hydrating}
          blocked={minorBlocked}
          embed={embed}
          conversationId={conversationId}
          onRecalculate={handleRecalculate}
          onRetryPlan={handleRetryPlan}
          onAcceptPlanOffer={handleRequestPlan}
          glp1={glp1}
          lang={lang}
        />
      </main>

      {error && (
        <div className="error-banner" role="alert">
          <strong>{t.error.headline}</strong>{' '}
          {error.toLowerCase().includes('overloaded') ||
          error.toLowerCase().includes('busy') ||
          error.toLowerCase().includes('please try sending')
            ? t.error.busy
            : error}
        </div>
      )}

      {!embed && (
      <p className="disclaimer">
        <small>
          {t.disclaimer.text}{' '}
          <span className="footer-sep" aria-hidden="true">·</span>{' '}
          <button
            type="button"
            className="link-inline"
            onClick={() => setMethodologyOpen(true)}
            aria-haspopup="dialog"
          >
            {t.disclaimer.methodology}
          </button>
        </small>
      </p>
      )}
      </div>

      {!embed && <SiteFooter lang={lang} />}
      {!embed && (
        <MethodologyPanel open={methodologyOpen} onClose={() => setMethodologyOpen(false)} lang={lang} />
      )}
    </div>
  )
}
